/**
 * Order Management Service
 * 
 * Provides comprehensive order management functionality for managers including:
 * - Fetching and filtering orders
 * - Order status management
 * - Order approval/rejection
 * - Bulk operations
 * - Statistics and analytics
 * - Export functionality
 * - Error handling and validation
 * 
 * @module OrderManagementService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc, // Add deleteDoc
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  Timestamp,
  writeBatch,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  DocumentReference,
  collectionGroup,
  addDoc,
  documentId,
  runTransaction,
  deleteField
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Order,
  OrderFilters,
  OrderSort,
  OrderStatistics,
  OrderStatus,
  OrderActionResult,
  BulkOrderAction,
  OrderAction
} from '../@types/order';
import { PDFService } from './PDFService';
import { EmailService } from './EmailService';
import { getLocalCalendarYMD, parseCalendarYMDToLocalDate } from '../utils/calendarDate';

/** Firestore orderBy field used for manager all-orders collection group pagination */
export type AllOrdersIndexedSortField =
  | 'orderId'
  | 'documentId'
  | 'orderDate'
  | 'finalizedAt'
  | 'createdAt';

/**
 * When resuming pagination we need to replay the exact same orderBy as the first page so
 * that `startAfter(cursor)` returns the right window of results.
 */
export type AllOrdersQueryPlan =
  | {
      kind: 'indexed';
      orderField: AllOrdersIndexedSortField;
    }
  | { kind: 'clientSort' };

export interface GetAllOrdersPagination {
  startAfterSnapshot?: QueryDocumentSnapshot | null;
  queryPlan?: AllOrdersQueryPlan | null;
}

export interface GetAllOrdersResult {
  orders: Order[];
  hasMore: boolean;
  lastSnapshot?: QueryDocumentSnapshot | null;
  queryPlan?: AllOrdersQueryPlan | null;
}

/**
 * Order Management Service Class
 * Handles all order-related operations for managers
 */
export class OrderManagementService {
  private static readonly STORES_COLLECTION = 'Stores';
  private static readonly ORDERS_COLLECTION = 'OrderHistory';
  private static readonly AUDIT_LOG_COLLECTION = 'AuditLogs';

  /**
   * Get a single order by ID
   * 
   * @param storeId - The store ID containing the order
   * @param orderId - The order ID
   * @returns Promise resolving to the order data or null if not found
   * @throws Error if database operation fails
   */
  static async getOrderById(storeId: string, orderId: string): Promise<Order | null> {
    try {
      if (!storeId || !orderId) {
        throw new Error('Store ID and Order ID are required');
      }

      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );
      const orderSnap = await getDoc(orderRef);

      if (!orderSnap.exists()) {
        console.warn(`Order not found: ${orderId} in store ${storeId}`);
        return null;
      }

      const data = orderSnap.data();
      
      // Fetch store data to get salesperson number
      let storeData = null;
      try {
        const storeRef = doc(db, this.STORES_COLLECTION, storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          storeData = storeSnap.data();
        }
      } catch (error) {
        console.warn(`Failed to fetch store ${storeId}:`, error);
      }
      
      return this.formatOrderData(orderId, storeId, data, storeData);
    } catch (error: any) {
      console.error('Error fetching order:', error);
      throw new Error(`Failed to fetch order: ${error.message}`);
    }
  }

  /**
   * Get order details by order ID across all stores
   * 
   * @param orderId - The order ID to search for
   * @returns Promise resolving to the order data or null if not found
   * @throws Error if database operation fails
   */
  static async getOrderDetailsById(orderId: string): Promise<Order | null> {
    try {
      if (!orderId) {
        throw new Error('Order ID is required');
      }

      // Search across all stores using collection group query
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      let querySnapshot = await getDocs(
        query(ordersQuery, where('orderId', '==', orderId), limit(1))
      );

      // Fallback 1: some historical docs use the identifier as document ID but not orderId field
      if (querySnapshot.empty) {
        querySnapshot = await getDocs(
          query(ordersQuery, where(documentId(), '==', orderId), limit(1))
        );
      }

      // Fallback 2: some docs have neither a 12-digit document ID nor orderId field.
      // For timestamp-style identifiers (YYYYMMDDHHMM), try matching finalizedAt minute window.
      if (querySnapshot.empty && /^\d{12}$/.test(orderId)) {
        const year = Number(orderId.slice(0, 4));
        const month = Number(orderId.slice(4, 6));
        const day = Number(orderId.slice(6, 8));
        const hour = Number(orderId.slice(8, 10));
        const minute = Number(orderId.slice(10, 12));
        const minuteStart = new Date(year, month - 1, day, hour, minute, 0, 0);

        if (!Number.isNaN(minuteStart.getTime())) {
          const minuteEnd = new Date(minuteStart.getTime() + 60_000);
          querySnapshot = await getDocs(
            query(
              ordersQuery,
              where('finalizedAt', '>=', Timestamp.fromDate(minuteStart)),
              where('finalizedAt', '<', Timestamp.fromDate(minuteEnd)),
              limit(1)
            )
          );
        }
      }

      if (querySnapshot.empty) {
        console.warn(`Order not found: ${orderId}`);
        return null;
      }

      const orderDoc = querySnapshot.docs[0];
      const data = orderDoc.data();
      
      // Extract storeId from the document path
      const pathSegments = orderDoc.ref.path.split('/');
      const storeId = pathSegments[pathSegments.length - 3];

      // Fetch store data to get salesperson number
      let storeData = null;
      try {
        const storeRef = doc(db, this.STORES_COLLECTION, storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          storeData = storeSnap.data();
        }
      } catch (error) {
        console.warn(`Failed to fetch store ${storeId}:`, error);
      }

      return this.formatOrderData(orderDoc.id, storeId, data, storeData);
    } catch (error: any) {
      console.error('Error fetching order details:', error);
      throw new Error(`Failed to fetch order details: ${error.message}`);
    }
  }

  /**
   * Get all orders across all stores with filtering and sorting
   *
   * @param filters - Optional filters to apply
   * @param sort - Optional sorting configuration
   * @param pageSize - Number of results per page (default: 50)
   * @param pagination - Optional Firestore cursor (must repeat queryPlan from the previous page)
   * @returns Promise resolving to orders, hasMore, and cursor metadata for load-more
   * @throws Error if database operation fails
   */
  static async getAllOrders(
    filters?: OrderFilters,
    sort?: OrderSort,
    pageSize: number = 50,
    pagination?: GetAllOrdersPagination
  ): Promise<GetAllOrdersResult> {
    try {
      // Using collectionGroup to query across all stores' orders
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters) {
        // Status filter
        if (filters.status) {
          if (Array.isArray(filters.status)) {
            constraints.push(where('status', 'in', filters.status));
          } else {
            constraints.push(where('status', '==', filters.status));
          }
        }

        // Date range filters
        if (filters.dateFrom) {
          constraints.push(where('orderDate', '>=', getLocalCalendarYMD(filters.dateFrom)));
        }
        if (filters.dateTo) {
          constraints.push(where('orderDate', '<=', getLocalCalendarYMD(filters.dateTo)));
        }

        // Sales rep filter
        if (filters.salesRepId) {
          constraints.push(where('salesRepId', '==', filters.salesRepId));
        }

        // Flagged only filter
        if (filters.flaggedOnly) {
          constraints.push(where('flaggedForReview', '==', true));
        }

        // Requires approval filter
        if (filters.requiresApprovalOnly) {
          constraints.push(where('requiresApproval', '==', true));
        }
      }

      // Sorting strategy:
      // - For date-range queries: orderBy('orderDate') is required (Firestore rule for range
      //   filters).
      // - Otherwise: orderBy('orderId', 'desc'). OrderHistory docs are saved with orderId in
      //   YYYYMMDDHHMM format, so a global string sort puts the newest orders at the top
      //   across all stores. IMPORTANT: we deliberately avoid orderBy(__name__) on the
      //   collection group, because in collection-group queries `__name__` is the full
      //   document path (Stores/{storeId}/OrderHistory/{id}) — sorting by it effectively
      //   sorts by storeId first, which hid today's orders from stores with lexicographically
      //   earlier IDs.
      const sortDirection = sort?.direction || 'desc';
      const hasOrderDateRange = !!(filters?.dateFrom || filters?.dateTo);
      const hasExplicitStatus = !!filters?.status;

      // Add limit
      constraints.push(limit(pageSize + 1));

      let querySnapshot;
      let needsClientSideSort = false;
      let indexedOrderField: AllOrdersIndexedSortField | null = null;

      const runOrderedQuery = (
        orderField: AllOrdersIndexedSortField,
        startAfterSnap?: QueryDocumentSnapshot | null,
        extraConstraints: QueryConstraint[] = []
      ) => {
        const order =
          orderField === 'documentId'
            ? orderBy(documentId(), sortDirection)
            : orderBy(orderField, sortDirection);
        const base: QueryConstraint[] = [
          ...constraints.slice(0, -1),
          ...extraConstraints,
          order
        ];
        if (startAfterSnap) {
          base.push(startAfter(startAfterSnap));
        }
        base.push(limit(pageSize + 1));
        return query(ordersQuery, ...base);
      };

      /** When no index exists, fetch a larger batch (unordered) and sort by YYYYMMDDHHMM id so recent orders are not missing. */
      const clientSortBatchCap = Math.min(1500, Math.max(400, (pageSize + 1) * 25));

      if (
        pagination?.startAfterSnapshot &&
        pagination.queryPlan?.kind === 'indexed'
      ) {
        const plan = pagination.queryPlan;
        indexedOrderField = plan.orderField;
        querySnapshot = await getDocs(
          runOrderedQuery(plan.orderField, pagination.startAfterSnapshot)
        );
      } else {
        try {
          if (hasOrderDateRange) {
            // Date-range query path: orderDate must be the first orderBy.
            querySnapshot = await getDocs(runOrderedQuery('orderDate'));
            indexedOrderField = 'orderDate';
          } else {
            // Global sort by the YYYYMMDDHHMM orderId string. Works with or without an
            // explicit status filter because both the `orderId DESC` and `status + orderId
            // DESC` collection-group indexes are configured.
            querySnapshot = await getDocs(runOrderedQuery('orderId'));
            indexedOrderField = 'orderId';
          }
        } catch (error: any) {
          if (error.code === 'failed-precondition' || error.message?.includes('index')) {
            try {
              console.warn('⚠️ primary index not available, trying orderBy(documentId)');
              querySnapshot = await getDocs(runOrderedQuery('documentId'));
              indexedOrderField = 'documentId';
            } catch (documentIdErr: any) {
              if (
                documentIdErr.code === 'failed-precondition' ||
                documentIdErr.message?.includes('index')
              ) {
                try {
                  console.warn('⚠️ documentId index not available, trying finalizedAt');
                  querySnapshot = await getDocs(runOrderedQuery('finalizedAt'));
                  indexedOrderField = 'finalizedAt';
                } catch (finalizedAtErr: any) {
                  if (
                    finalizedAtErr.code === 'failed-precondition' ||
                    finalizedAtErr.message?.includes('index')
                  ) {
                    try {
                      console.warn('⚠️ finalizedAt index not available, trying createdAt');
                      querySnapshot = await getDocs(runOrderedQuery('createdAt'));
                      indexedOrderField = 'createdAt';
                    } catch (createdAtErr: any) {
                      if (
                        createdAtErr.code === 'failed-precondition' ||
                        createdAtErr.message?.includes('index')
                      ) {
                        console.warn(
                          '⚠️ No indexed sort field; fetching batch and sorting client-side by order id'
                        );
                        needsClientSideSort = true;
                        indexedOrderField = null;
                        const q = query(
                          ordersQuery,
                          ...constraints.slice(0, -1),
                          limit(clientSortBatchCap)
                        );
                        querySnapshot = await getDocs(q);
                      } else {
                        throw createdAtErr;
                      }
                    }
                  } else {
                    throw finalizedAtErr;
                  }
                }
              } else {
                throw documentIdErr;
              }
            }
          } else {
            throw error;
          }
        }
      }

      const orders: Order[] = [];
      let docs = querySnapshot.docs;

      const sortKeyForDoc = (d: (typeof docs)[0]): string => {
        const data = d.data();
        const oid = data?.orderId != null ? String(data.orderId) : '';
        if (/^\d{12}$/.test(oid)) return oid;
        if (/^\d{12}$/.test(d.id)) return d.id;
        return oid || d.id || '';
      };

      if (needsClientSideSort) {
        docs = [...docs].sort((a, b) =>
          sortDirection === 'desc'
            ? sortKeyForDoc(b).localeCompare(sortKeyForDoc(a))
            : sortKeyForDoc(a).localeCompare(sortKeyForDoc(b))
        );
      }

      // -----------------------------------------------------------------
      // First-page safety net — "today's orders" backfill
      // -----------------------------------------------------------------
      // On the first page only, explicitly pull every order whose `orderId` starts with
      // today's YYYYMMDD prefix and merge them in. This guarantees that newly finalized
      // orders always land on page 1 even if the primary query's limit(51) happens to cut
      // them off (for example because a sudden burst of orders was written under stores
      // with lexicographically later IDs). The query uses the existing collection-group
      // index on `orderId` so no extra index is required.
      const isFirstPage = !pagination?.startAfterSnapshot;
      if (isFirstPage && !hasOrderDateRange && !needsClientSideSort) {
        try {
          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, '0');
          const d = String(now.getDate()).padStart(2, '0');
          const todayPrefix = `${y}${m}${d}`; // YYYYMMDD
          // Lexicographic upper bound = today's prefix with trailing '\uf8ff' so we catch
          // anything with orderId = "YYYYMMDD..." but nothing from later days.
          const todayUpper = `${todayPrefix}\uf8ff`;

          const todayConstraints: QueryConstraint[] = [
            where('orderId', '>=', todayPrefix),
            where('orderId', '<', todayUpper),
            orderBy('orderId', 'desc'),
            limit(500)
          ];
          // Preserve other active filters that don't conflict with the orderId range.
          if (filters?.salesRepId) {
            todayConstraints.unshift(where('salesRepId', '==', filters.salesRepId));
          }
          if (filters?.flaggedOnly) {
            todayConstraints.unshift(where('flaggedForReview', '==', true));
          }
          if (filters?.requiresApprovalOnly) {
            todayConstraints.unshift(where('requiresApproval', '==', true));
          }
          if (hasExplicitStatus) {
            const s = filters!.status;
            if (Array.isArray(s)) {
              if (s.length > 0) todayConstraints.unshift(where('status', 'in', s));
            } else {
              todayConstraints.unshift(where('status', '==', s));
            }
          }

          const todaySnap = await getDocs(query(ordersQuery, ...todayConstraints));

          const seenKeys = new Set<string>(
            docs.map((dd) => {
              const parts = dd.ref.path.split('/');
              return `${parts[parts.length - 3]}|${dd.id}`;
            })
          );
          const backfillDocs: typeof docs = [];
          for (const dd of todaySnap.docs) {
            const parts = dd.ref.path.split('/');
            const key = `${parts[parts.length - 3]}|${dd.id}`;
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              backfillDocs.push(dd);
            }
          }

          if (backfillDocs.length > 0) {
            console.warn(
              `⚠️ Order Management backfilled ${backfillDocs.length} of today's order(s) missed by primary query`
            );
            docs = [...docs, ...backfillDocs].sort((a, b) =>
              sortDirection === 'desc'
                ? sortKeyForDoc(b).localeCompare(sortKeyForDoc(a))
                : sortKeyForDoc(a).localeCompare(sortKeyForDoc(b))
            );
          }
        } catch (err: any) {
          // Non-fatal: if this backfill fails (e.g. missing index, transient error), we
          // still return the primary results.
          console.warn(
            'Today-range safety-net query failed, returning primary results only:',
            err?.message || err
          );
        }
      }

      const hasMore = needsClientSideSort
        ? docs.length > pageSize || querySnapshot.docs.length >= clientSortBatchCap
        : docs.length > pageSize;

      // Process results (exclude the extra document for pagination when using indexed orderBy).
      // After backfill we may have more than pageSize+1 docs — slice to pageSize+1 for consistent
      // hasMore handling and to keep the returned page size stable.
      const docsToProcessArray = needsClientSideSort
        ? docs.slice(0, pageSize)
        : hasMore
          ? docs.slice(0, pageSize)
          : docs;

      // Fetch store data for all unique stores to get salesperson numbers
      const storeIds = new Set<string>();
      
      for (const docSnap of docsToProcessArray) {
        const pathSegments = docSnap.ref.path.split('/');
        const storeId = pathSegments[pathSegments.length - 3];
        storeIds.add(storeId);
      }
      
      // Fetch all stores in parallel
      const storeDataMap = new Map<string, any>();
      const storePromises = Array.from(storeIds).map(async (storeId) => {
        try {
          const storeRef = doc(db, this.STORES_COLLECTION, storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            storeDataMap.set(storeId, storeSnap.data());
          }
        } catch (error) {
          console.warn(`Failed to fetch store ${storeId}:`, error);
        }
      });
      await Promise.all(storePromises);

      for (const docSnap of docsToProcessArray) {
        const data = docSnap.data();
        // Extract storeId from the document path
        const pathSegments = docSnap.ref.path.split('/');
        const storeId = pathSegments[pathSegments.length - 3];

        const order = this.formatOrderData(docSnap.id, storeId, data, storeDataMap.get(storeId));
        orders.push(order);
      }

      // needsClientSideSort: snapshot docs were already ordered by YYYYMMDDHHMM order id above.
      if (
        !needsClientSideSort &&
        sort?.field &&
        ['totalAmount', 'storeName', 'status', 'orderDate'].includes(sort.field)
      ) {
        const dir = sort.direction === 'asc' ? 1 : -1;
        const toOrderDateMs = (raw: unknown): number => {
          if (!raw) return 0;
          if (typeof raw === 'string') {
            const local = parseCalendarYMDToLocalDate(raw.trim());
            if (local) return local.getTime();
            const parsed = new Date(raw);
            return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
          }
          if (raw instanceof Date) return raw.getTime();
          if (typeof (raw as { toDate?: () => Date }).toDate === 'function') {
            return (raw as { toDate: () => Date }).toDate().getTime();
          }
          return 0;
        };
        orders.sort((a, b) => {
          if (sort.field === 'totalAmount') {
            return (a.totalAmount - b.totalAmount) * dir;
          }
          if (sort.field === 'storeName') {
            return String(a.storeName || '').localeCompare(String(b.storeName || ''), undefined, {
              sensitivity: 'base'
            }) * dir;
          }
          if (sort.field === 'orderDate') {
            return (toOrderDateMs(a.orderDate) - toOrderDateMs(b.orderDate)) * dir;
          }
          return String(a.status || '').localeCompare(String(b.status || '')) * dir;
        });
      }

      // Apply client-side filters for complex queries
      let filteredOrders = orders;

      if (filters) {
        // Search term filter - comprehensive search across multiple fields (case-insensitive)
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.trim().toLowerCase();
          
          // Helper function to safely check if a field contains the search term
          const fieldContains = (field: any): boolean => {
            if (field === null || field === undefined) return false;
            return String(field).toLowerCase().includes(searchLower);
          };
          
          // Helper for exact match (useful for numeric IDs)
          const fieldMatches = (field: any): boolean => {
            if (field === null || field === undefined) return false;
            return String(field).toLowerCase() === searchLower;
          };
          
          filteredOrders = orders.filter(order =>
            // Order ID
            fieldContains(order.orderId) ||
            // Store name
            fieldContains(order.storeName) ||
            // PO Number
            fieldContains(order.poNumber) ||
            fieldContains(order.purchaseOrder) ||
            // Customer number
            fieldContains(order.customerNumber) ||
            fieldMatches(order.customerNumber) ||
            // Salesperson number
            fieldContains(order.salespersonNo) ||
            fieldMatches(order.salespersonNo) ||
            // Sales rep name
            fieldContains(order.salesRepName) ||
            // City
            fieldContains(order.city) ||
            // Province
            fieldContains(order.province) ||
            // Address
            fieldContains(order.address) ||
            // Email
            fieldContains(order.email) ||
            // Order type
            fieldContains(order.orderType)
          );
        }

        // Salesperson number filter (supports multi-select)
        if (filters.salespersonNo) {
          const salespersonNos = Array.isArray(filters.salespersonNo) 
            ? filters.salespersonNo.map(s => String(s).toLowerCase())
            : [String(filters.salespersonNo).toLowerCase()];
          
          filteredOrders = filteredOrders.filter(order => {
            const orderSalesperson = String(order.salespersonNo || '').toLowerCase();
            return salespersonNos.some(sp => orderSalesperson === sp || orderSalesperson.includes(sp));
          });
        }

        // City filter (supports multi-select)
        if (filters.city) {
          const cities = Array.isArray(filters.city)
            ? filters.city.map(c => c.toLowerCase())
            : [filters.city.toLowerCase()];
          
          filteredOrders = filteredOrders.filter(order => {
            const orderCity = (order.city || '').toLowerCase();
            return cities.some(city => orderCity === city || orderCity.includes(city));
          });
        }

        // Province filter (supports multi-select)
        if (filters.province) {
          const provinces = Array.isArray(filters.province)
            ? filters.province.map(p => p.toLowerCase())
            : [filters.province.toLowerCase()];
          
          filteredOrders = filteredOrders.filter(order => {
            const orderProvince = (order.province || '').toLowerCase();
            return provinces.some(prov => orderProvince === prov || orderProvince.includes(prov));
          });
        }

        // Order type filter (supports multi-select)
        if (filters.orderType) {
          const orderTypes = Array.isArray(filters.orderType)
            ? filters.orderType.map(t => t.toLowerCase())
            : [filters.orderType.toLowerCase()];
          
          filteredOrders = filteredOrders.filter(order => {
            const orderType = (order.orderType || '').toLowerCase();
            return orderTypes.some(type => orderType === type);
          });
        }

        // Amount range filters
        if (filters.minAmount !== undefined) {
          filteredOrders = filteredOrders.filter(order => order.totalAmount >= filters.minAmount!);
        }
        if (filters.maxAmount !== undefined) {
          filteredOrders = filteredOrders.filter(order => order.totalAmount <= filters.maxAmount!);
        }
      }

      // 12-digit order id in search: merge direct lookup so the order appears even if not in the first page
      if (filters?.searchTerm) {
        const term = filters.searchTerm.trim();
        if (/^\d{12}$/.test(term)) {
          try {
            const direct = await this.getOrderDetailsById(term);
            if (direct) {
              const exists = filteredOrders.some(
                (o) => o.orderId === direct.orderId && o.storeId === direct.storeId
              );
              if (!exists) {
                filteredOrders = [direct, ...filteredOrders];
              }
            }
          } catch {
            // non-fatal
          }
        }
      }

      let lastSnapshot: QueryDocumentSnapshot | null = null;
      if (!needsClientSideSort && docsToProcessArray.length > 0) {
        lastSnapshot = docsToProcessArray[docsToProcessArray.length - 1] ?? null;
      }

      const queryPlan: AllOrdersQueryPlan = needsClientSideSort
        ? { kind: 'clientSort' }
        : {
            kind: 'indexed',
            orderField: indexedOrderField!
          };

      return {
        orders: filteredOrders,
        hasMore,
        lastSnapshot,
        queryPlan
      };
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      throw new Error(`Failed to fetch orders: ${error.message}`);
    }
  }

  /**
   * Get orders for a specific store
   * 
   * @param storeId - The store ID
   * @param filters - Optional filters
   * @returns Promise resolving to array of orders
   */
  static async getOrdersByStore(
    storeId: string,
    filters?: OrderFilters
  ): Promise<Order[]> {
    try {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      const constraints: QueryConstraint[] = [];

      // Apply status filter if provided
      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          constraints.push(where('status', 'in', filters.status));
        } else {
          constraints.push(where('status', '==', filters.status));
        }
      }

      // Default sorting by order date
      constraints.push(orderBy('orderDate', 'desc'));

      const ordersRef = collection(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION
      );
      const q = query(ordersRef, ...constraints);
      const querySnapshot = await getDocs(q);

      // Fetch store data to get salesperson number
      let storeData = null;
      try {
        const storeRef = doc(db, this.STORES_COLLECTION, storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          storeData = storeSnap.data();
        }
      } catch (error) {
        console.warn(`Failed to fetch store ${storeId}:`, error);
      }

      const orders: Order[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        orders.push(this.formatOrderData(doc.id, storeId, data, storeData));
      });

      return orders;
    } catch (error: any) {
      console.error('Error fetching store orders:', error);
      throw new Error(`Failed to fetch store orders: ${error.message}`);
    }
  }

  /**
   * Update order status
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param newStatus - New status to set
   * @param userId - ID of the user performing the action
   * @param notes - Optional notes about the status change
   * @returns Promise resolving to action result
   */
  static async updateOrderStatus(
    storeId: string,
    orderId: string,
    newStatus: OrderStatus,
    userId: string,
    notes?: string
  ): Promise<OrderActionResult> {
    try {
      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      const updateData: any = {
        status: newStatus,
        updatedAt: Timestamp.now(),
        reviewedBy: userId,
        reviewedAt: Timestamp.now()
      };

      // Add timestamp for specific status changes
      if (newStatus === 'approved') {
        updateData.approvedAt = Timestamp.now();
      }

      // Add manager notes if provided
      if (notes) {
        updateData.managerNotes = notes;
      }

      await updateDoc(orderRef, updateData);

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'updateOrderStatus',
        entity: 'Order',
        entityId: orderId,
        changes: { status: newStatus },
        notes: notes || `Order status changed to ${newStatus}`
      });

      return {
        success: true,
        orderId,
        message: `Order status updated to ${newStatus}`
      };
    } catch (error: any) {
      console.error('Error updating order status:', error);
      return {
        success: false,
        error: error.message || 'Failed to update order status'
      };
    }
  }

  /**
   * Update order details (edit order)
   * Allows managers to edit order information including items, quantities, etc.
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param updates - Partial order data to update
   * @param userId - ID of the user performing the update
   * @returns Promise resolving to action result
   */
  static async updateOrder(
    storeId: string,
    orderId: string,
    updates: Partial<Order>,
    userId: string
  ): Promise<OrderActionResult> {
    try {
      let orderRef: DocumentReference;
      let currentData: any;
      
      // Try to get the order document directly first (orderId might be the document ID)
      const directRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      // Check if order exists by trying to get it directly
      const orderSnap = await getDoc(directRef);
      if (orderSnap.exists()) {
        // Found by document ID
        orderRef = directRef;
        currentData = orderSnap.data();
      } else {
        // If not found by document ID, try searching by orderId field
        const historyRef = collection(db, this.STORES_COLLECTION, storeId, this.ORDERS_COLLECTION);
        const q = query(historyRef, where('orderId', '==', orderId), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Found by orderId field
          const foundDoc = querySnapshot.docs[0];
          orderRef = foundDoc.ref;
          currentData = foundDoc.data();
        } else {
          // Last resort: try to find by getting all documents and matching
          // This handles case sensitivity issues
          const allDocsSnapshot = await getDocs(historyRef);
          const matchingDoc = allDocsSnapshot.docs.find(doc => {
            const data = doc.data();
            return doc.id === orderId || 
                   doc.id.toLowerCase() === orderId.toLowerCase() ||
                   data.orderId === orderId ||
                   (data.orderId && data.orderId.toLowerCase() === orderId.toLowerCase());
          });
          
          if (matchingDoc) {
            orderRef = matchingDoc.ref;
            currentData = matchingDoc.data();
          } else {
            throw new Error(`Order not found: ${orderId}`);
          }
        }
      }
      
      // Prepare update data
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
        reviewedBy: userId,
        reviewedAt: Timestamp.now(),
        editedBy: userId,
        editedAt: Timestamp.now()
      };

      // Recalculate totals if items were updated
      // Check if any item arrays are being updated (even if empty arrays)
      const hasItemUpdates = 'heritageItems' in updates || 
                            'livingstonItems' in updates || 
                            'flowerItems' in updates || 
                            'vegetableItems' in updates ||
                            'supplyItems' in updates;
      
      if (hasItemUpdates) {
        // Use the updated items if provided, otherwise fall back to current data
        const allItems = [
          ...(updates.heritageItems !== undefined ? (updates.heritageItems || []) : (currentData.heritageItems || [])),
          ...(updates.livingstonItems !== undefined ? (updates.livingstonItems || []) : (currentData.livingstonItems || [])),
          ...(updates.flowerItems !== undefined ? (updates.flowerItems || []) : (currentData.flowerItems || [])),
          ...(updates.vegetableItems !== undefined ? (updates.vegetableItems || []) : (currentData.vegetableItems || [])),
          ...(updates.supplyItems !== undefined ? (updates.supplyItems || []) : (currentData.supplyItems || []))
        ];

        updateData.totalItems = allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        updateData.totalAmount = allItems.reduce(
          (sum, item) => sum + ((item.price || 0) * (item.quantity || 0)),
          0
        );
      }

      await updateDoc(orderRef, updateData);

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'updateOrder',
        entity: 'Order',
        entityId: orderId,
        changes: updates,
        notes: 'Order details updated by manager'
      });

      return {
        success: true,
        orderId,
        message: 'Order updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating order:', error);
      return {
        success: false,
        error: error.message || 'Failed to update order'
      };
    }
  }

  /**
   * Approve an order
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param userId - ID of the user approving
   * @param notes - Optional approval notes
   * @returns Promise resolving to action result
   */
  static async approveOrder(
    storeId: string,
    orderId: string,
    userId: string,
    notes?: string,
    managerDisplayName?: string
  ): Promise<OrderActionResult> {
    try {
      let orderRef: DocumentReference;
      let orderData: any = null;
      
      // Try to get the order document directly first (orderId might be the document ID)
      const directRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      // Check if order exists by trying to get it directly
      const orderSnap = await getDoc(directRef);
      if (orderSnap.exists()) {
        // Found by document ID
        orderRef = directRef;
        orderData = { ...orderSnap.data(), orderId: orderSnap.id };
      } else {
        // If not found by document ID, try searching by orderId field
        const historyRef = collection(db, this.STORES_COLLECTION, storeId, this.ORDERS_COLLECTION);
        const q = query(historyRef, where('orderId', '==', orderId), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // Found by orderId field
          const foundDoc = querySnapshot.docs[0];
          orderRef = foundDoc.ref;
          orderData = { ...foundDoc.data(), orderId: foundDoc.id };
        } else {
          // Last resort: try to find by getting all documents and matching
          // This handles case sensitivity issues
          const allDocsSnapshot = await getDocs(historyRef);
          const matchingDoc = allDocsSnapshot.docs.find(doc => {
            const data = doc.data();
            return doc.id === orderId || 
                   doc.id.toLowerCase() === orderId.toLowerCase() ||
                   data.orderId === orderId ||
                   (data.orderId && data.orderId.toLowerCase() === orderId.toLowerCase());
          });
          
          if (matchingDoc) {
            orderRef = matchingDoc.ref;
            orderData = { ...matchingDoc.data(), orderId: matchingDoc.id };
          } else {
            throw new Error(`Order not found: ${orderId}`);
          }
        }
      }

      await updateDoc(orderRef, {
        status: 'approved',
        requiresApproval: false,
        reviewedBy: userId,
        reviewedByName: managerDisplayName || null,
        reviewedAt: Timestamp.now(),
        approvedAt: Timestamp.now(),
        managerNotes: notes || 'Order approved',
        updatedAt: Timestamp.now()
      });

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'approveOrder',
        entity: 'Order',
        entityId: orderId,
        notes: notes || 'Order approved by manager'
      });

      return {
        success: true,
        orderId,
        message: 'Order approved successfully'
      };
    } catch (error: any) {
      console.error('Error approving order:', error);
      return {
        success: false,
        error: error.message || 'Failed to approve order'
      };
    }
  }

  /**
   * Sends the approval pack (HTML + PDF) to the portal inbox with Customer Service CC'd.
   * Updates OrderHistory with customerServiceEmailSentAt on success.
   */
  static async sendApprovedOrderEmailToCustomerService(
    storeId: string,
    orderId: string,
    userId: string
  ): Promise<OrderActionResult> {
    try {
      let orderRef: DocumentReference;
      let orderData: any = null;

      const directRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      const orderSnap = await getDoc(directRef);
      if (orderSnap.exists()) {
        orderRef = directRef;
        orderData = { ...orderSnap.data(), orderId: orderSnap.id };
      } else {
        const historyRef = collection(db, this.STORES_COLLECTION, storeId, this.ORDERS_COLLECTION);
        const q = query(historyRef, where('orderId', '==', orderId), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const foundDoc = querySnapshot.docs[0];
          orderRef = foundDoc.ref;
          orderData = { ...foundDoc.data(), orderId: foundDoc.id };
        } else {
          const allDocsSnapshot = await getDocs(historyRef);
          const matchingDoc = allDocsSnapshot.docs.find((d) => {
            const data = d.data();
            return (
              d.id === orderId ||
              d.id.toLowerCase() === orderId.toLowerCase() ||
              data.orderId === orderId ||
              (data.orderId && data.orderId.toLowerCase() === orderId.toLowerCase())
            );
          });

          if (matchingDoc) {
            orderRef = matchingDoc.ref;
            orderData = { ...matchingDoc.data(), orderId: matchingDoc.id };
          } else {
            throw new Error(`Order not found: ${orderId}`);
          }
        }
      }

      if (orderData.status !== 'approved') {
        return {
          success: false,
          error: 'Only approved orders can be sent to Customer Service.'
        };
      }

      // Hard lock to prevent duplicate sends (double-click/race/retry).
      const lockResult = await runTransaction(db, async (tx) => {
        const snap = await tx.get(orderRef);
        if (!snap.exists()) {
          throw new Error('Order not found while preparing email send.');
        }
        const latest = snap.data() as any;
        if (latest.customerServiceEmailSentAt) {
          return { canSend: false, reason: 'already-sent' as const };
        }
        if (latest.customerServiceEmailSendingAt) {
          return { canSend: false, reason: 'in-progress' as const };
        }

        tx.update(orderRef, {
          customerServiceEmailSendingAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        return { canSend: true as const };
      });

      if (!lockResult.canSend) {
        return {
          success: false,
          error:
            lockResult.reason === 'already-sent'
              ? 'Customer Service email was already sent for this order.'
              : 'Customer Service email is already being sent. Please wait.'
        };
      }

      const pdfOrderId = orderData.orderId || orderId;
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData,
        orderId: pdfOrderId,
        customerEmail: orderData.email || orderData.customerEmail || ''
      });

      EmailService.initialize();
      await EmailService.sendOrderApprovalEmailToCustomerService(
        pdfBlob,
        pdfOrderId,
        orderData,
        undefined
      );

      await updateDoc(orderRef, {
        customerServiceEmailSentAt: Timestamp.now(),
        customerServiceEmailSendingAt: deleteField(),
        updatedAt: Timestamp.now()
      });

      await this.logAuditEntry({
        userId,
        action: 'sendToCustomerService',
        entity: 'Order',
        entityId: pdfOrderId,
        notes: 'Approval pack emailed to portal and Customer Service'
      });

      return {
        success: true,
        orderId: pdfOrderId,
        message: 'Sent to Customer Service'
      };
    } catch (error: any) {
      console.error('Error sending to Customer Service:', error);
      try {
        // Best effort release of send lock if email failed.
        let releaseRef: DocumentReference | null = null;
        const directRef = doc(
          db,
          this.STORES_COLLECTION,
          storeId,
          this.ORDERS_COLLECTION,
          orderId
        );
        const directSnap = await getDoc(directRef);
        if (directSnap.exists()) {
          releaseRef = directRef;
        } else {
          const historyRef = collection(db, this.STORES_COLLECTION, storeId, this.ORDERS_COLLECTION);
          const q = query(historyRef, where('orderId', '==', orderId), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            releaseRef = snap.docs[0].ref;
          }
        }
        if (releaseRef) {
          await updateDoc(releaseRef, {
            customerServiceEmailSendingAt: deleteField(),
            updatedAt: Timestamp.now()
          });
        }
      } catch (releaseErr) {
        console.warn('Failed to release customer-service email lock:', releaseErr);
      }
      return {
        success: false,
        error: error.message || 'Failed to send to Customer Service'
      };
    }
  }

  /**
   * Reject an order
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param userId - ID of the user rejecting
   * @param reason - Reason for rejection
   * @returns Promise resolving to action result
   */
  static async rejectOrder(
    storeId: string,
    orderId: string,
    userId: string,
    reason: string
  ): Promise<OrderActionResult> {
    try {
      if (!reason) {
        throw new Error('Rejection reason is required');
      }

      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      await updateDoc(orderRef, {
        status: 'cancelled',
        requiresApproval: false,
        reviewedBy: userId,
        reviewedAt: Timestamp.now(),
        cancelledAt: Timestamp.now(),
        managerNotes: `Rejected: ${reason}`,
        updatedAt: Timestamp.now()
      });

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'rejectOrder',
        entity: 'Order',
        entityId: orderId,
        notes: `Order rejected: ${reason}`
      });

      return {
        success: true,
        orderId,
        message: 'Order rejected'
      };
    } catch (error: any) {
      console.error('Error rejecting order:', error);
      return {
        success: false,
        error: error.message || 'Failed to reject order'
      };
    }
  }

  /**
   * Flag an order for review
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param userId - ID of the user flagging
   * @param reason - Reason for flagging
   * @returns Promise resolving to action result
   */
  static async flagOrder(
    storeId: string,
    orderId: string,
    userId: string,
    reason: string
  ): Promise<OrderActionResult> {
    try {
      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      await updateDoc(orderRef, {
        flaggedForReview: true,
        flagReason: reason,
        updatedAt: Timestamp.now()
      });

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'flagOrder',
        entity: 'Order',
        entityId: orderId,
        notes: `Order flagged: ${reason}`
      });

      return {
        success: true,
        orderId,
        message: 'Order flagged for review'
      };
    } catch (error: any) {
      console.error('Error flagging order:', error);
      return {
        success: false,
        error: error.message || 'Failed to flag order'
      };
    }
  }

  /**
   * Unflag an order
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param userId - ID of the user unflagging
   * @returns Promise resolving to action result
   */
  static async unflagOrder(
    storeId: string,
    orderId: string,
    userId: string
  ): Promise<OrderActionResult> {
    try {
      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      await updateDoc(orderRef, {
        flaggedForReview: false,
        flagReason: '',
        updatedAt: Timestamp.now()
      });

      // Log audit trail
      await this.logAuditEntry({
        userId,
        action: 'unflagOrder',
        entity: 'Order',
        entityId: orderId,
        notes: 'Order unflagged'
      });

      return {
        success: true,
        orderId,
        message: 'Order unflagged'
      };
    } catch (error: any) {
      console.error('Error unflagging order:', error);
      return {
        success: false,
        error: error.message || 'Failed to unflag order'
      };
    }
  }

  /**
   * Perform bulk action on multiple orders
   * 
   * @param bulkAction - Bulk action configuration
   * @param userId - ID of the user performing the action
   * @returns Promise resolving to results for each order
   */
  static async performBulkAction(
    bulkAction: BulkOrderAction,
    userId: string
  ): Promise<OrderActionResult[]> {
    const results: OrderActionResult[] = [];

    try {
      for (const orderId of bulkAction.orderIds) {
        try {
          // Extract storeId from orderId if needed (format: storeId_orderId)
          // For now, we'll need to query to find the order
          const order = await this.findOrderById(orderId);
          
          if (!order) {
            results.push({
              success: false,
              orderId,
              error: 'Order not found'
            });
            continue;
          }

          let result: OrderActionResult;

          switch (bulkAction.action) {
            case 'approve':
              result = await this.approveOrder(
                order.storeId,
                orderId,
                userId,
                bulkAction.notes,
                bulkAction.managerDisplayName
              );
              break;

            case 'reject':
              result = await this.rejectOrder(
                order.storeId,
                orderId,
                userId,
                bulkAction.notes || 'Bulk rejection'
              );
              break;

            case 'process':
            case 'complete':
            case 'cancel': {
              const resolvedStatus: OrderStatus =
                bulkAction.newStatus ??
                (bulkAction.action === 'process'
                  ? 'processing'
                  : bulkAction.action === 'complete'
                    ? 'completed'
                    : 'cancelled');
              result = await this.updateOrderStatus(
                order.storeId,
                orderId,
                resolvedStatus,
                userId,
                bulkAction.notes
              );
              break;
            }

            case 'flag':
              result = await this.flagOrder(
                order.storeId,
                orderId,
                userId,
                bulkAction.notes || 'Flagged for review'
              );
              break;

            case 'unflag':
              result = await this.unflagOrder(order.storeId, orderId, userId);
              break;

            case 'delete':
              result = await this.deleteOrder(order.storeId, orderId, userId);
              break;

            default:
              result = {
                success: false,
                orderId,
                error: `Unsupported action: ${bulkAction.action}`
              };
          }

          results.push(result);
        } catch (error: any) {
          results.push({
            success: false,
            orderId,
            error: error.message
          });
        }
      }

      return results;
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      throw new Error(`Bulk action failed: ${error.message}`);
    }
  }

  /**
   * Delete an order
   * 
   * @param storeId - The store ID
   * @param orderId - The order ID
   * @param userId - ID of the user performing the action
   * @returns Promise resolving to action result
   */
  static async deleteOrder(
    storeId: string,
    orderId: string,
    userId: string
  ): Promise<OrderActionResult> {
    try {
      if (!storeId || !orderId) {
        throw new Error('Store ID and Order ID are required');
      }

      const orderRef = doc(
        db,
        this.STORES_COLLECTION,
        storeId,
        this.ORDERS_COLLECTION,
        orderId
      );

      // Verify order exists before deletion
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) {
        return {
          success: false,
          orderId,
          error: 'Order not found'
        };
      }

      await deleteDoc(orderRef);

      return {
        success: true,
        orderId,
        message: 'Order deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting order:', error);
      return {
        success: false,
        orderId,
        error: `Failed to delete order: ${error.message}`
      };
    }
  }

  /**
   * Get order statistics for dashboard
   * 
   * @param dateRange - Optional date range for filtering
   * @returns Promise resolving to order statistics
   */
  static async getOrderStatistics(
    dateRange?: { from: Date; to: Date }
  ): Promise<OrderStatistics> {
    try {
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const querySnapshot = await getDocs(ordersQuery);

      const stats: OrderStatistics = {
        totalOrders: 0,
        pendingOrders: 0,
        approvedOrders: 0,
        processingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        ordersToday: 0,
        ordersThisWeek: 0,
        ordersThisMonth: 0,
        revenueToday: 0,
        revenueThisWeek: 0,
        revenueThisMonth: 0
      };

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      querySnapshot.forEach((doc) => {
        const order = doc.data();
        const rawDate = order.orderDate;
        let orderDate: Date;
        if (typeof rawDate === 'string') {
          const local = parseCalendarYMDToLocalDate(rawDate.trim());
          orderDate = local ?? new Date(rawDate);
        } else if (rawDate && typeof rawDate === 'object' && 'toDate' in rawDate) {
          orderDate = (rawDate as { toDate: () => Date }).toDate();
        } else if (rawDate instanceof Date) {
          orderDate = rawDate;
        } else {
          orderDate = new Date(0);
        }

        stats.totalOrders++;

        // Count by status (includes legacy string values stored in Firestore)
        switch (order.status) {
          case 'submitted':
          case 'pending':
            stats.pendingOrders++;
            break;
          case 'approved':
            stats.approvedOrders++;
            break;
          case 'processing':
            stats.processingOrders++;
            break;
          case 'completed':
            stats.completedOrders++;
            break;
          case 'cancelled':
            stats.cancelledOrders++;
            break;
        }

        // Calculate revenue
        const orderAmount = order.totalAmount || 0;
        stats.totalRevenue += orderAmount;

        // Time-based statistics
        if (orderDate >= todayStart) {
          stats.ordersToday++;
          stats.revenueToday += orderAmount;
        }
        if (orderDate >= weekStart) {
          stats.ordersThisWeek++;
          stats.revenueThisWeek += orderAmount;
        }
        if (orderDate >= monthStart) {
          stats.ordersThisMonth++;
          stats.revenueThisMonth += orderAmount;
        }
      });

      // Calculate average
      stats.averageOrderValue = stats.totalOrders > 0 
        ? stats.totalRevenue / stats.totalOrders 
        : 0;

      return stats;
    } catch (error: any) {
      console.error('Error calculating order statistics:', error);
      throw new Error(`Failed to calculate statistics: ${error.message}`);
    }
  }

  /**
   * Find an order by ID across all stores
   * 
   * @param orderId - The order ID
   * @returns Promise resolving to the order or null
   * @private
   */
  private static async findOrderById(orderId: string): Promise<Order | null> {
    try {
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const q = query(ordersQuery, where('orderId', '==', orderId), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const orderDoc = querySnapshot.docs[0];
      const data = orderDoc.data();
      const pathSegments = orderDoc.ref.path.split('/');
      const storeId = pathSegments[pathSegments.length - 3];

      // Fetch store data to get salesperson number
      let storeData = null;
      try {
        const storeRef = doc(db, this.STORES_COLLECTION, storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          storeData = storeSnap.data();
        }
      } catch (error) {
        console.warn(`Failed to fetch store ${storeId}:`, error);
      }

      return this.formatOrderData(orderDoc.id, storeId, data, storeData);
    } catch (error: any) {
      console.error('Error finding order:', error);
      return null;
    }
  }

  /**
   * Format order data from Firestore document
   * 
   * @param orderId - The order ID
   * @param storeId - The store ID
   * @param data - Raw Firestore data
   * @param storeData - Optional store data to get salesperson info
   * @returns Formatted Order object
   * @private
   */
  private static formatOrderData(orderId: string, storeId: string, data: any, storeData?: any): Order {
    // Calculate totals from items
    const allItems = [
      ...(data.heritageItems || []),
      ...(data.livingstonItems || []),
      ...(data.flowerItems || []),
      ...(data.vegetableItems || []),
      ...(data.supplyItems || [])
    ];

    const totalItems = allItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalAmount = allItems.reduce(
      (sum, item) => sum + ((item.price || 0) * (item.quantity || 0)),
      0
    );

    // Get salesperson number from store data if available
    const salespersonNo = storeData?.SalespersonNo || storeData?.salespersonNo || data.salesRepId || data.salespersonNo;
    const salesPersonName = storeData?.SalesPersonName || storeData?.salesPersonName || data.salesRepName || data.salesPersonName;

    // Ensure orderId is always in timestamp format (YYYYMMDDHHMM)
    let finalOrderId = orderId;
    const poNumber = data?.poNumber || data?.purchaseOrder;
    
    // Check if orderId is already in timestamp format (12 digits)
    const isTimestampFormat = /^\d{12}$/.test(orderId);
    
    if (!isTimestampFormat) {
      // Generate timestamp format from order date
      const orderDate = data.finalizedAt || data.createdAt || data.updatedAt || data.orderDate;
      if (orderDate) {
        let dateObj: Date;
        if (orderDate instanceof Date) {
          dateObj = orderDate;
        } else if (typeof orderDate === 'string') {
          dateObj =
            parseCalendarYMDToLocalDate(orderDate.trim()) ?? new Date(orderDate);
        } else if (orderDate && typeof orderDate === 'object' && 'toDate' in orderDate) {
          dateObj = (orderDate as any).toDate();
        } else {
          dateObj = new Date();
        }
        
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const hours = String(dateObj.getHours()).padStart(2, '0');
          const minutes = String(dateObj.getMinutes()).padStart(2, '0');
          finalOrderId = `${year}${month}${day}${hours}${minutes}`;
        }
      }
    }

    return {
      orderId: finalOrderId, // Always timestamp format (YYYYMMDDHHMM)
      storeId,
      ...data,
      poNumber: poNumber || '', // PO number is separate, keep empty if not provided
      purchaseOrder: data?.purchaseOrder || poNumber || '',
      totalItems,
      totalAmount,
      // Use salesperson from store data if available, otherwise fall back to order data
      salespersonNo: salespersonNo || data.salespersonNo || data.salesRepId,
      salesRepName: salesPersonName || data.salesRepName || data.salesPersonName,
      // Convert Firestore Timestamps to Dates
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      submittedAt: data.submittedAt?.toDate?.() || data.submittedAt,
      completedAt: data.completedAt?.toDate?.() || data.completedAt,
      cancelledAt: data.cancelledAt?.toDate?.() || data.cancelledAt,
      reviewedAt: data.reviewedAt?.toDate?.() || data.reviewedAt,
      reviewedByName: data.reviewedByName || undefined,
      approvedAt: data.approvedAt?.toDate?.() || data.approvedAt,
      customerServiceEmailSentAt:
        data.customerServiceEmailSentAt?.toDate?.() || data.customerServiceEmailSentAt,
      finalizedAt: data.finalizedAt?.toDate?.() || data.finalizedAt,
    } as Order;
  }

  /**
   * Log audit entry for order actions
   * 
   * @param entry - Audit log entry data
   * @private
   */
  private static async logAuditEntry(entry: {
    userId: string;
    action: string;
    entity: string;
    entityId: string;
    changes?: Record<string, any>;
    notes?: string;
  }): Promise<void> {
    try {
      const auditRef = collection(db, this.AUDIT_LOG_COLLECTION);
      await addDoc(auditRef, {
        ...entry,
        timestamp: Timestamp.now()
      });
    } catch (error: any) {
      console.warn('Failed to log audit entry:', error.message);
      // Don't throw - audit logging is non-critical
    }
  }
}

